import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import type {
	FormattedWebItem,
	WebItemLocationChildrenFn,
} from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import {
	SYSTEM_ATTACHMENT,
	SYSTEM_ATTACHMENT_METADATA,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

import { toPreviewAttachmentId } from '../hooks/useLegacyAttachmentPreview.ts';

const styles = cssMap({
	actionButton: {
		display: 'inline-flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		height: '24px',
		margin: token('space.0'),
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body'),
		whiteSpace: 'nowrap',
		textDecoration: 'none',
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

// webItemSections(<location>) returns EVERY web item at the location — including Confluence's own
// bundled attachment actions (edit/remove/preview), which the React macro already renders as native
// Preview/View/Properties/Delete buttons. Rendering those again would duplicate them, so we keep only
// third-party (Connect) app items. This prefix is how AFM identifies Connect app web items elsewhere
// (see filterConnectAppsFromExtensionPoints).
const CONNECT_APP_KEY_PREFIX = 'com.atlassian.plugins.atlassian-connect-plugin:';

// The two per-attachment locations (`system.attachment` buttons and `system.attachment.metadata`
// lozenges) are fetched in a single batched WebItemLocation query keyed by the attachment content id
// — both slots issue this same query with identical variables, so Apollo dedupes them into one
// request per row. The legacy attachments macro only wires these two locations; `system.attachment`
// + `.metadata`. (`system.attachment.history` belongs to the separate view-attachments page, not the
// macro, so it is intentionally not handled here.)
const ATTACHMENT_LOCATIONS = [SYSTEM_ATTACHMENT, SYSTEM_ATTACHMENT_METADATA];

// Each slot renders a different subset in a different part of the UI:
//  - `actions`  → buttons in the expanded row's action bar (once per attachment)
//  - `metadata` → lozenges inline next to the file name (once per attachment)
type AttachmentWebItemSlot = 'actions' | 'metadata';

// `item.section` is formatted as "<location>[/<subsection>]", so an item belongs to a location when
// its section equals that location or is prefixed by "<location>/". The `/` boundary matters because
// `system.attachment` is a string prefix of `system.attachment.metadata`. (Matches attachments-page.)
const isItemAtLocation = (item: FormattedWebItem, location: string): boolean =>
	item.section === location || (item.section?.startsWith(`${location}/`) ?? false);

const itemSlot = (item: FormattedWebItem): AttachmentWebItemSlot | null => {
	if (isItemAtLocation(item, SYSTEM_ATTACHMENT_METADATA)) {
		return 'metadata';
	}
	if (isItemAtLocation(item, SYSTEM_ATTACHMENT)) {
		return 'actions';
	}
	return null;
};

const filterItemsForSlot = (
	webItems: readonly FormattedWebItem[],
	slot: AttachmentWebItemSlot,
): FormattedWebItem[] => webItems.filter((item) => itemSlot(item) === slot);

type AttachmentWebItemsProps = {
	/** GraphQL attachment node id (ARI / att-prefixed) — converted to the bare content id. */
	attachmentId: string;
	/**
	 * Which subset to render: `actions` (buttons, in the expanded row's action bar) or `metadata`
	 * (lozenges, next to the file name).
	 */
	slot: AttachmentWebItemSlot;
};

// Web items only emit their `attribute.*` params as DOM attributes when they declare
// `has-custom-attributes: true`, exactly as the legacy #renderCustomAttributes macro gated it.
const hasCustomAttributes = (item: FormattedWebItem): boolean =>
	item.params?.['has-custom-attributes'] === 'true';

// Strip the `attribute.` prefix so `attribute.data-linked-resource-id` becomes the real
// `data-linked-resource-id` DOM attribute (mirrors #renderCustomAttributes). Connect apps rely
// on these for their client-side click handling.
const toCustomAttributes = (item: FormattedWebItem): Record<string, string> => {
	const attributes: Record<string, string> = {};
	const params = item.params ?? {};
	Object.keys(params).forEach((key) => {
		if (key.startsWith('attribute.')) {
			attributes[key.slice('attribute.'.length)] = params[key];
		}
	});
	return attributes;
};

const IS_HTTP_URL = /^https?:\/\//;

// Action button — mirrors the `system.attachment` loop in attachments-table.vm:
//   <a class="aui-button $styleClass" href="$url" [custom-attrs]>$label</a>
const renderButton = (item: FormattedWebItem): React.JSX.Element => {
	const key = item.completeKey ?? item.id ?? item.url ?? item.label ?? '';
	const testId = `attachment-web-item-${item.completeKey ?? item.id ?? ''}`;
	const customAttributes = hasCustomAttributes(item) ? toCustomAttributes(item) : {};

	if (item.url) {
		if (IS_HTTP_URL.test(item.url) && !item.styleClass) {
			return (
				<Anchor key={key} href={item.url} testId={testId} {...customAttributes}>
					{item.label}
				</Anchor>
			);
		}

		return (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- Connect web-item click delegation matches on the anchor's `ap-*` classes and reads its resolved href for context; the ADS Anchor strips className so it cannot be used here.
			<a
				key={key}
				href={item.url}
				css={styles.actionButton}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- web-item styleClass carries the Connect binding classes (ap-dialog/ap-link-webitem/...) the host JS matches on; visual styling is provided by `css`
				className={item.styleClass || undefined}
				data-testid={testId}
				{...customAttributes}
			>
				{item.label}
			</a>
		);
	}

	return (
		<button
			key={key}
			type="button"
			css={styles.actionButton}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- web-item styleClass carries the Connect binding classes the host JS matches on; visual styling is provided by `css`
			className={item.styleClass || undefined}
			data-testid={testId}
			{...customAttributes}
		>
			{item.label}
		</button>
	);
};

// Shared inner content for metadata items, rendered inside either an <a> or a <span>.
const renderMetadataContent = (item: FormattedWebItem): React.JSX.Element => {
	const iconClass = item.params?.['iconClass'];
	return (
		<>
			{item.icon?.path ? (
				<img
					alt=""
					src={item.icon.path}
					height={item.icon.height ?? undefined}
					width={item.icon.width ?? undefined}
				/>
			) : null}
			<span>
				{iconClass ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- legacy aui-iconfont icon class, matching renderItemSpan
					<span className={`aui-icon aui-icon-small aui-iconfont-${iconClass}`} />
				) : null}
				{item.label}
			</span>
		</>
	);
};

const renderMetadata = (item: FormattedWebItem): React.JSX.Element => {
	const key = `${item.completeKey}-${item.id}`;
	const testId = `attachment-web-item-${item.completeKey}-${item.id}`;
	const customAttributes = hasCustomAttributes(item) ? toCustomAttributes(item) : {};

	return (
		<span
			key={key}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- legacy `filename` wrapper class, matching renderItemSpan
			className="filename"
		>
			{item.url ? (
				// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- Connect web-item click delegation matches on the anchor's `ap-*` classes and reads its resolved href for context; the ADS Anchor strips className so it cannot be used here.
				<a
					id={item.id ?? undefined}
					href={item.url}
					title={item.tooltip ?? undefined}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- web-item styleClass (e.g. aui-lozenge, ap-*) drives styling and Connect JS click delegation, matching the legacy macro
					className={item.styleClass || undefined}
					data-testid={testId}
					{...customAttributes}
				>
					{renderMetadataContent(item)}
				</a>
			) : (
				<span
					id={item.id ?? undefined}
					title={item.tooltip ?? undefined}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- web-item styleClass (e.g. aui-lozenge) drives styling, matching the legacy macro
					className={item.styleClass || undefined}
					data-testid={testId}
					{...customAttributes}
				>
					{renderMetadataContent(item)}
				</span>
			)}
		</span>
	);
};

// Renders the Connect app web items for a slot. Kept as its own component (rather than inline in the
// WebItemLocation render prop) so the Connect-app filter can be memoised — filtering in the render
// body trips the "expensive array method during render" lint.
const AttachmentWebItemList = ({
	webItems,
	slot,
}: {
	webItems: readonly FormattedWebItem[];
	slot: AttachmentWebItemSlot;
}): React.JSX.Element | null => {
	const items = useMemo(() => {
		// Host actions are already rendered natively by the macro, so keep only Connect items to
		// avoid duplicating them, then narrow to the locations this slot renders.
		const connectItems = webItems.filter((item) =>
			item.completeKey?.startsWith(CONNECT_APP_KEY_PREFIX),
		);
		return filterItemsForSlot(connectItems, slot);
	}, [webItems, slot]);

	if (items.length === 0) {
		return null;
	}
	const renderItem = slot === 'metadata' ? renderMetadata : renderButton;
	return (
		<Inline space="space.100" alignBlock="center">
			{items.map(renderItem)}
		</Inline>
	);
};

/**
 * Renders the Connect/plugin web items for a single attachment at the per-attachment locations
 * (system.attachment, system.attachment.metadata) in a single batched query. The legacy attachments
 * macro rendered these server-side into the macro HTML; the migrated (React) macro fetches them via
 * WebItemLocation and reproduces the legacy markup (classes, icon, custom attributes) so appearance
 * and Connect JS hooks match. Pass `slot` to select which subset to render: `actions` (buttons) or
 * `metadata` (lozenges).
 */
export const AttachmentWebItems = ({
	attachmentId,
	slot,
}: AttachmentWebItemsProps): React.JSX.Element | null => {
	// The GraphQL node id is an ARI/att-prefixed id; the backend ContentId scalar needs the bare
	// numeric attachment content id.
	const contentId = toPreviewAttachmentId(attachmentId);
	if (!contentId) {
		return null;
	}

	// Pass ONLY contentId (the attachment). Do NOT pass spaceKey: the backend's getWebView
	// prioritises spaceKey and routes to forSpace(...), which never puts the attachment into
	// context. With contentId alone it takes forContent(attachmentId); the space is still resolved
	// server-side (an attachment is a SpaceContentEntityObject).
	// Typed explicitly: WebItemLocation's `children` prop is a union, so an inline callback's
	// `webItems` would otherwise fall back to `any` (TS7031).
	const renderWebItems: WebItemLocationChildrenFn = ({ webItems }) => (
		<AttachmentWebItemList webItems={webItems} slot={slot} />
	);
	return (
		<WebItemLocation location={ATTACHMENT_LOCATIONS} contentId={contentId}>
			{renderWebItems}
		</WebItemLocation>
	);
};
